import React, { ReactNode } from "react"
import { AppLayout } from "app/core/layouts/AppLayout"
import { BlitzPage } from "@blitzjs/next"
import Head from "next/head"
import { HiPhone, HiMail, HiChat } from "react-icons/hi"
import { ButtonLink } from "app/core/components/ButtonLink"

const Section = ({ children }: { children: ReactNode }) => (
  <div className="bg-white shadow rounded-lg px-6 py-6 space-y-4">{children}</div>
)

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <div className="pb-3 border-b border-gray-200">
    <h3 className="text-lg font-bold leading-6 text-gray-900">{children}</h3>
  </div>
)

const formatText = (text) => {
  // Split into tokens: ~~...~~ or **...**
  const parts = text.split(/(~~.*?~~|\*\*.*?\*\*)/g)

  return parts.map((part, i) => {
    // Strikethrough
    if (part.startsWith("~~") && part.endsWith("~~")) {
      return (
        <span key={i} className="line-through">
          {part.slice(2, -2)}
        </span>
      )
    }

    // Bold
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={i} className="font-semibold">
          {part.slice(2, -2)}
        </span>
      )
    }

    // Normal text (also handle newlines)
    return part.split("\n").map((line, j) => (
      <span key={`${i}-${j}`}>
        {line}
        {j < part.split("\n").length - 1 && <br />}
      </span>
    ))
  })
}

const guidelineImages = [
  {
    url:
      "https://homeschoolboss.com/wp-content/uploads/2024/12/Dos-and-Donts-for-Math-Graphic-ac804b.svg",
    alt: "Math Guidelines",
  },
  {
    url:
      "https://homeschoolboss.com/wp-content/uploads/2024/12/Reading-Guidelines-Email-Card-f6e278.svg",
    alt: "Reading Guidelines",
  },
  {
    url:
      "https://homeschoolboss.com/wp-content/uploads/2024/12/Language-Usage-Guidelines-Email-Card-33e1b5.svg",
    alt: "Language Usage Guidelines",
  },
  {
    url:
      "https://homeschoolboss.com/wp-content/uploads/2024/12/Science-Guidelines-Email-Card-812010.svg",
    alt: "Science Guidelines",
  },
]

const faqs = [
  { question: "How many questions are on the test?", answer: "40–45 questions" },
  {
    question: "How long does it take to complete this test?",
    answer: "60–90 minutes per subject, on average",
  },
  {
    question: "Are the tests untimed?",
    answer:
      "Overall, yes! Student may take their time.\nHowever, there are two timed aspects that must be honored.\n1.  Each question has a 15-minute window. If a student is logged out we will load a new question for them with no impact to their score.\n2.  A test must be completed within 28 days OR before a test season ends. Season dates can be found below.\n\n2025-2026 MAP Growth Seasons\n~~Fall 2025: August 11 - November 26~~\n~~Winter 2025: December 1 - February 27, 2026~~\nSpring 2026: March 2 - July 31",
  },
  {
    question: "Can my student take longer than 60-90 minutes?",
    answer: "Absolutely - everyone works differently.",
  },
  {
    question: "What kind of test is this? What is it like?",
    answer:
      "MAP Growth is an adaptive test and will adjust to a level where your student is answering approximately half of the questions correctly. Some of the material may be well outside of their grade level and that is okay! It is okay to not know the answers and take their best guess.",
  },
  { question: "Can my student take breaks?", answer: "Definitely! Breaks are highly encouraged." },
  {
    question: "How long can a break be?",
    answer:
      "There is no time limit.\nHowever, the test platform will log your student out of their test after 15 minutes of inactivity.",
  },
  {
    question: "Do we let you know when we take breaks?",
    answer:
      "It depends!\n**Less than 15 minutes:** Request a “pause” before leaving for your break.\n**15 minutes or longer:** Please let us know when you are leaving. We will set your test up to be signed back into when you return.",
  },
  {
    question: "The test logged me out! Where is my name?! HELP!",
    answer:
      "Don't panic! Student progress is saved after each question is submitted.\nReach out to your Proctor for help getting back in.",
  },
  {
    question: "How do I reach out to my Proctor?",
    answer: "You may call, email, or chat in from our home page.",
  },
  {
    question: "When will I receive my student(s) reports?",
    answer: "1-2 days after each test is completed, barring any unforeseen technical issues.",
  },
  // {
  //   question: "Is there a practice Test?",
  //   answer: '["MAP Growth Practice Test" button]\nUsername: grow\nPassword: grow',
  // },
]

const TestDayLaunchPage: BlitzPage = () => {
  return (
    <div className="px-4 pt-2 pb-6 space-y-6 md:py-6 sm:px-6 lg:px-8">
      <div className="md:hidden flex flex-col items-center justify-center py-16 text-center space-y-3">
        <p className="text-lg font-semibold text-gray-700">Desktop or Tablet Required</p>
        <p className="text-sm text-gray-500 max-w-xs">
          This page is only available on tablet or desktop devices. Please switch to a larger screen
          to continue.
        </p>
      </div>
      <div className="hidden md:flex md:flex-col md:space-y-6">
        <Head>
          <title>Test Day Launch Pad</title>
        </Head>

        <div id="header-bar" className="flex flex-col text-2xl font-semibold">
          <h1 className="text-4xl font-bold text-gray-900">Test Day Launch Pad</h1>
          NWEA MAP Growth Assessments
        </div>

        <p className="text-gray-600 mt-4">
          Are you scheduled for a MAP Growth test today? Here&apos;s how you will get started.
        </p>

        <ButtonLink
          color="#F65058"
          href="https://test.mapnwea.org/#/nopopup"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full px-7 md:w-auto mt-4 mb-4"
        >
          Log in to MAP Growth
        </ButtonLink>

        <Section>
          <SectionHeading>Login Information</SectionHeading>
          <p className="text-sm text-gray-600">
            Please select <span className="font-semibold">just one</span> contact method to receive
            your login information. If you opted in for automatic text messages and have already
            received your credentials, you do not need to contact us unless you have questions. ☺︎
          </p>
          <ul className="mt-2 space-y-3">
            <li className="flex items-center space-x-3 text-sm text-gray-700">
              <HiPhone className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <span>
                <span className="font-medium">Call or Text:</span> 612-430-6652
              </span>
            </li>
            <li className="flex items-center space-x-3 text-sm text-gray-700">
              <HiMail className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <span>
                <span className="font-medium">Email:</span> sales@homeschoolboss.com
              </span>
            </li>
            <li className="flex items-center space-x-3 text-sm text-gray-700">
              <HiChat className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <span>
                <span className="font-medium">Chat:</span> Click the blue chat icon in the
                bottom-right corner
              </span>
            </li>
          </ul>
        </Section>

        <Section>
          <SectionHeading>
            Please read the testing guidelines below before getting started
          </SectionHeading>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-2">
            {guidelineImages.map((img) => (
              <div key={img.url} className="overflow-hidden">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={img.url} alt={img.alt} className="w-full h-auto" />
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <SectionHeading>Frequently Asked Questions</SectionHeading>
          <dl className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question}>
                <dt className="text-sm font-semibold text-gray-900">{faq.question}</dt>
                <dd className="mt-1 text-sm text-gray-600 whitespace-pre-line">
                  {formatText(faq.answer)}
                </dd>
              </div>
            ))}
            <dt className="text-sm font-semibold text-gray-900">Is there a practice Test?</dt>
            <dd className="mt-1 text-sm text-gray-600 whitespace-pre-line">
              <ButtonLink
                color="#F65058"
                href="https://practice.mapnwea.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4"
              >
                MAP Growth Practice Test
              </ButtonLink>
              <br />
              <span>Username: grow</span>
              <br />
              <span>Password: grow</span>
            </dd>
          </dl>
        </Section>
      </div>
    </div>
  )
}

TestDayLaunchPage.authenticate = true
TestDayLaunchPage.getLayout = (page) => <AppLayout title="Test Day Launch Pad">{page}</AppLayout>

export default TestDayLaunchPage
